import { useState } from "react";
import { useLeadForm, STATES, PRODUCTS } from "@/lib/constants";
import { Send, CheckCircle, Loader2 } from "lucide-react";

export default function LeadForm({ variant = "hero" }) {
  const { form, updateField, submitForm, loading, submitted, setSubmitted, error } = useLeadForm();

  if (submitted) {
    return (
      <div data-testid={`lead-form-${variant}-success`} className={`rounded-2xl border border-teal-200 bg-teal-50 p-8 text-center ${variant === "bottom" ? "" : "shadow-lg"}`}>
        <CheckCircle className="w-14 h-14 text-teal-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-teal-800 font-['Outfit']">Thank You!</h3>
        <p className="text-stone-600 mt-2">Our team will contact you within 30 minutes during business hours.</p>
        <button
          data-testid={`lead-form-${variant}-reset`}
          onClick={() => setSubmitted(false)}
          className="mt-4 text-teal-700 underline text-sm font-medium"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  const isBottom = variant === "bottom";

  return (
    <form
      data-testid={`lead-form-${variant}`}
      onSubmit={submitForm}
      className={`rounded-2xl border ${isBottom ? "border-teal-700/30 bg-white/10 backdrop-blur-sm" : "border-stone-200 bg-white shadow-lg"} p-6 sm:p-8`}
    >
      <h3 className={`text-xl font-semibold font-['Outfit'] mb-1 ${isBottom ? "text-white" : "text-stone-900"}`}>
        {isBottom ? "Get Your Free Stairlift Quote" : "Get a Free Quote"}
      </h3>
      <p className={`text-sm mb-5 ${isBottom ? "text-teal-100" : "text-stone-500"}`}>
        {isBottom ? "Stair lifts from ₹1.55 Lakhs - our experts will call you" : "Stair lifts from ₹1.55 Lakhs - we'll call you back"}
      </p>

      {error && (
        <div data-testid={`lead-form-${variant}-error`} className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <input
          data-testid={`lead-form-${variant}-name`}
          type="text"
          placeholder="Full Name *"
          value={form.full_name}
          onChange={(e) => updateField("full_name", e.target.value)}
          className={`w-full rounded-xl px-4 py-3 text-sm ${isBottom ? "bg-white/10 border-teal-600/30 text-white placeholder:text-teal-200/60 focus:border-teal-400 focus:ring-teal-400" : "bg-stone-50 border-stone-300 text-stone-900 focus:border-teal-500 focus:ring-teal-500"} border transition-colors`}
          required
        />
        <input
          data-testid={`lead-form-${variant}-phone`}
          type="tel"
          placeholder="Phone Number *"
          value={form.phone}
          onChange={(e) => updateField("phone", e.target.value)}
          className={`w-full rounded-xl px-4 py-3 text-sm ${isBottom ? "bg-white/10 border-teal-600/30 text-white placeholder:text-teal-200/60 focus:border-teal-400 focus:ring-teal-400" : "bg-stone-50 border-stone-300 text-stone-900 focus:border-teal-500 focus:ring-teal-500"} border transition-colors`}
          required
        />
        <input
          data-testid={`lead-form-${variant}-email`}
          type="email"
          placeholder="Email (Optional)"
          value={form.email}
          onChange={(e) => updateField("email", e.target.value)}
          className={`w-full rounded-xl px-4 py-3 text-sm ${isBottom ? "bg-white/10 border-teal-600/30 text-white placeholder:text-teal-200/60 focus:border-teal-400 focus:ring-teal-400" : "bg-stone-50 border-stone-300 text-stone-900 focus:border-teal-500 focus:ring-teal-500"} border transition-colors`}
        />

        <div className="grid grid-cols-2 gap-3">
          <select
            data-testid={`lead-form-${variant}-state`}
            value={form.state}
            onChange={(e) => updateField("state", e.target.value)}
            className={`w-full rounded-xl px-4 py-3 text-sm ${isBottom ? "bg-white/10 border-teal-600/30 text-white focus:border-teal-400" : "bg-stone-50 border-stone-300 text-stone-900 focus:border-teal-500"} border transition-colors`}
          >
            <option value="" className="text-stone-900">Select State</option>
            {STATES.map(s => <option key={s} value={s} className="text-stone-900">{s}</option>)}
          </select>
          <select
            data-testid={`lead-form-${variant}-product`}
            value={form.product}
            onChange={(e) => updateField("product", e.target.value)}
            className={`w-full rounded-xl px-4 py-3 text-sm ${isBottom ? "bg-white/10 border-teal-600/30 text-white focus:border-teal-400" : "bg-stone-50 border-stone-300 text-stone-900 focus:border-teal-500"} border transition-colors`}
          >
            <option value="" className="text-stone-900">Select Product</option>
            {PRODUCTS.map(p => <option key={p} value={p} className="text-stone-900">{p}</option>)}
          </select>
        </div>

        <button
          data-testid={`lead-form-${variant}-submit`}
          type="submit"
          disabled={loading}
          className={`w-full flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-semibold text-sm transition-colors ${isBottom ? "bg-amber-500 hover:bg-amber-600 text-stone-900" : "bg-[#b22300] hover:bg-[#8a1b00] text-white"} disabled:opacity-60`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {loading ? "Submitting..." : "Get Free Quote"}
        </button>
        <p className={`text-center text-xs mt-2 ${isBottom ? "text-teal-200/60" : "text-stone-400"}`}>
          No obligation. Free consultation. We respect your privacy.
        </p>
      </div>
    </form>
  );
}
